import { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { collection, query, where, getDocs, orderBy, doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { format } from 'date-fns';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import OrderDetails from '../components/orders/OrderDetails';

// Compact order item component
function OrderItem({ order, onClick }) {
  // Use created_at field from Firestore which is a Firestore Timestamp
  const date = order.created_at?.toDate ? order.created_at.toDate() : 
             order.timestamp ? new Date(order.timestamp) : new Date();
  const formattedDate = format(date, 'MMM d, yyyy');
  const formattedTime = format(date, 'h:mm a');
  
  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-800',
    processing: 'bg-blue-100 text-blue-800',
    completed: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800',
  };

  return (
    <div 
      onClick={onClick}
      className="bg-white p-4 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
    >
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-900 truncate">
              #{order.orderNumber || order.id?.substring(0, 8) || 'ORDER'}
            </span>
            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
              statusColors[order.status] || statusColors.pending
            }`}>
              {order.status || 'pending'}
            </span>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            {order.customer?.name || order.customerName || order.customer?.displayName || 'Customer'}
          </p>
        </div>
        <div className="ml-4 text-right">
          <p className="text-sm font-medium text-gray-900">
            {order.currency || 'BHD'} {parseFloat(order.subtotal_local || 0).toFixed(2)}
          </p>
          <p className="text-xs text-gray-500">
            {formattedDate} • {formattedTime}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function OrdersList() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const { currentAgent } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { orderId } = useParams();
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Open modal when selectedOrder changes
  useEffect(() => {
    setIsModalOpen(!!selectedOrder);
  }, [selectedOrder]);

  // Load orders for the current agent
  // Load orders and check for orderId in URL
  useEffect(() => {
    const fetchOrders = async () => {
      if (!currentAgent?.id) return;
      
      try {
        setLoading(true);
        const ordersRef = collection(db, 'orders');
        const q = query(
          ordersRef,
          where('agent_id', '==', currentAgent.id),
          orderBy('created_at', 'desc')
        );
        
        const querySnapshot = await getDocs(q);
        const ordersData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setOrders(ordersData);
        
        // If there's an orderId in the URL, find and set that order
        if (orderId) {
          const order = ordersData.find(o => o.id === orderId);
          if (order) {
            setSelectedOrder(order);
          } else {
            // If order not found in the list, try to fetch it directly
            try {
              const orderDoc = await getDoc(doc(db, 'orders', orderId));
              if (orderDoc.exists()) {
                setSelectedOrder({ id: orderDoc.id, ...orderDoc.data() });
              }
            } catch (error) {
              console.error('Error fetching order:', error);
            }
          }
        }
      } catch (error) {
        console.error('Error loading orders:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [currentAgent, orderId]);

  const filteredOrders = orders.filter(order => {
    const matchesSearch = 
      (order.orderNumber?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (order.customerName?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (order.id?.toLowerCase() || '').includes(searchTerm.toLowerCase());
      
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const handleOrderClick = (order) => {
    setSelectedOrder(order);
  };
  
  const handleCloseModal = () => {
    setSelectedOrder(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Order Details Modal */}
      {isModalOpen && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mt-16 mb-8">
            <div className="sticky top-0 bg-white p-4 border-b flex justify-between items-center z-10">
              <h2 className="text-lg font-semibold">Order Details</h2>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-500"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6">
              <OrderDetails order={selectedOrder} />
            </div>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <h1 className="text-lg font-semibold text-gray-900">My Orders</h1>
          </div>
          
          {/* Search and Filter */}
          <div className="mt-4 flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Search orders..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="w-full sm:w-48">
              <select
                className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>
        </div>
      </div>
      
      {/* Orders List */}
      <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          {filteredOrders.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">No orders found</p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {filteredOrders.map((order) => (
                <li key={order.id}>
                  <OrderItem 
                    order={order} 
                    onClick={() => handleOrderClick(order)}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
        
        {/* Order Summary */}
        {filteredOrders.length > 0 && (
          <div className="mt-4 bg-white p-4 rounded-md shadow">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
              <p className="text-sm text-gray-600">
                Showing {filteredOrders.length} of {orders.length} orders
              </p>
              <div className="flex flex-wrap gap-2 sm:gap-4">
                <span className="text-sm font-medium text-gray-900">
                  Total: {orders.length}
                </span>
                <span className="text-sm font-medium text-blue-600">
                  {((filteredOrders.length / Math.max(1, orders.length)) * 100).toFixed(0)}% of all orders
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
